import { ActivityIndicator, Pressable, useColorScheme } from 'react-native';
import { Paragraph, View, XStack, YStack } from 'tamagui';
import { Paperclip, X } from 'lucide-react-native';

import { AuthenticatedImage } from '@/components/authenticated-image';
import type { useAttachmentPicker } from '@/hooks/use-attachment-picker';
import { errorMessage } from '@/lib/photo-errors';
import { borderWidth, darkColors, lightColors, radius } from '@/theme/tokens.generated';
import { hit } from '@/lib/hit-slop';

const THUMB = 64;

// The one composer affordance for image attachments, shared by every
// surface that takes them (design.md, "Rich content"). Stateless: the
// surface owns useAttachmentPicker and passes it in, so it can read
// picker.photoIds when it sends.
export function AttachmentPicker({
  picker,
  locked = false,
}: {
  picker: ReturnType<typeof useAttachmentPicker>;
  // True while the surface is sending: adding or removing then would race
  // the in-flight request that references these photos.
  locked?: boolean;
}) {
  const colors = useColorScheme() === 'dark' ? darkColors : lightColors;
  const canAdd = picker.canAdd && !locked;
  const message = picker.error ? errorMessage(picker.error) : '';

  return (
    <YStack gap="$2">
      <XStack gap="$2" alignItems="center" flexWrap="wrap">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={picker.isUploading ? 'Uploading photo' : 'Attach a photo'}
          accessibilityState={{ disabled: !canAdd, busy: picker.isUploading }}
          aria-disabled={!canAdd}
          aria-busy={picker.isUploading}
          disabled={!canAdd}
          {...hit(4)}
          onPress={picker.add}
          style={({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) => ({
            width: 44,
            height: 44,
            borderRadius: radius.full,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: hovered || pressed ? colors.interactiveSelectedBg : 'transparent',
            opacity: !canAdd && !picker.isUploading ? 0.5 : pressed || hovered ? 0.8 : 1,
          })}
        >
          {picker.isUploading ? (
            <ActivityIndicator color={colors.actionPrimary} />
          ) : (
            <Paperclip size={24} strokeWidth={1.75} color={colors.actionPrimary} />
          )}
        </Pressable>

        {picker.photoIds.map((id) => (
          <View key={id} width={THUMB} height={THUMB}>
            <AuthenticatedImage
              path={`/photos/${id}`}
              accessibilityLabel="Photo to attach"
              width={THUMB}
              aspectRatio={1}
              objectFit="cover"
              borderRadius="$md"
            />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Remove photo"
              {...hit(12)}
              disabled={locked}
              onPress={() => picker.remove(id)}
              style={{
                position: 'absolute',
                top: -16,
                right: -16,
                width: 44,
                height: 44,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <View
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: radius.md,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: colors.surfaceCard,
                  borderWidth: borderWidth.hairline,
                  borderColor: colors.borderStrong,
                }}
              >
                <X size={16} strokeWidth={1.75} color={colors.textPrimary} />
              </View>
            </Pressable>
          </View>
        ))}
      </XStack>

      {message !== '' && (
        <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta" accessibilityRole="alert">
          {message}
        </Paragraph>
      )}
    </YStack>
  );
}
